import { Box, Grid, Paper, TextField, styled, useTheme, MenuItem } from '@mui/material';
import React from 'react';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import { CompanyDetail, CreateCompanyPayload } from 'src/dto/Company';
import Row from 'src/frontend-common/components/Row';
import { Formik, FormikErrors } from 'formik';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/frontend-common/components/Router';
import { useCreateCompanyAdminMutation, useUpdateCompanyAdminMutation } from 'src/redux/services/companiesAdmin';
import { AlertBox, FormMessage } from 'src/components/AlertBox';

import { CountrySelect, CountryType } from 'src/components/CountrySelect';
import ButtonComponent from 'src/components/ButtonComponent';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import { setActiveCompany } from 'src/frontend-common/localstorage';

export interface CompanyFormProps {
  companyData?: CompanyDetail;
  reducedVersion?: boolean;
  sendFeedBack?: (update: boolean) => void;
}

const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

const ReducedContainer = styled(Paper)(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
  marginTop: theme.spacing(2),
}));

export const CompanyForm: React.FC<CompanyFormProps> = ({ companyData, reducedVersion, sendFeedBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [errorMessages, setErrorMessages] = React.useState<FormMessage[]>([]);

  const [createCompanyAdmin, createdCompanyAdmin] = useCreateCompanyAdminMutation();
  const [updateCompanyAdmin, updatedCompanyAdmin] = useUpdateCompanyAdminMutation();

  const sizeListMap = [
    { id: '1-10', name: '1-10 employees' },
    { id: '11-50', name: '11-50 employees' },
    { id: '51-200', name: '51-200 employees' },
    { id: '201-500', name: '201-500 employees' },
    { id: '501-1000', name: '501-1000 employees' },
    { id: '1001-5000', name: '1001-5000 employees' },
    { id: '5001-10,000', name: '5001-10,000 employees' },
    { id: '10,001+', name: '10,001+ employees' },
  ];
  const usageListMap = [
    { id: 'payment_fraud', name: 'Payment Fraud' },
    { id: 'identity_verification', name: 'Identity Verification' },
    { id: 'transaction_risk_analysis', name: 'Transaction Risk Analysis' },
    { id: 'aml', name: 'AML' },
    { id: 'credit_risk_assesment', name: 'Credit Risk Assesment' },
    { id: 'data_enrichment', name: 'Data Enrichment' },
    { id: 'other', name: 'Other' },
  ];

  React.useEffect(() => {
    if (createdCompanyAdmin.isSuccess) {
      setTimeout(() => {
        setActiveCompany({
          id: createdCompanyAdmin.data.id,
          name: createdCompanyAdmin.data.name,
        });
        goBack();
      }, 2000);
    }
  }, [createdCompanyAdmin.isSuccess]);

  const handleUpdate = (id: string, values: CreateCompanyPayload) => {
    updateCompanyAdmin({ id, ...values, billing_key: null });
  };

  const handleCreate = (values: CreateCompanyPayload) => {
    createCompanyAdmin({ ...values, billing_key: null });
  };

  const goBack = () => navigate(paths.companies.path);

  const handleErrorMessageDismiss = (i: number) => {
    const newErrorMessages = [...errorMessages];
    newErrorMessages.splice(i, 1);
    setErrorMessages(newErrorMessages);
  };

  const formCore = (
    <Formik
      initialValues={{
        name: companyData ? companyData.name : '',
        country: companyData ? companyData.country : '',
        city: companyData ? companyData.city : '',
        state: companyData ? companyData.state : '',
        address_1: companyData ? companyData.address_1 : '',
        address_2: companyData ? companyData.address_2 : '',
        tax_id: companyData ? companyData.tax_id : '',
        sdi: companyData ? companyData.sdi : '',
        phone: companyData ? companyData.phone : '',
        active: companyData ? companyData.active : true,
        size: companyData ? companyData.size : '1-10',
        usage: companyData ? companyData.usage : 'payment_fraud',
      }}
      enableReinitialize
      validateOnChange={true}
      validate={(values) => {
        const errors: FormikErrors<CreateCompanyPayload> = {};
        if (values.name.length === 0) {
          errors.name = 'Required';
        }

        return errors;
      }}
      onSubmit={(values) => {
        if (companyData) {
          handleUpdate(companyData.id, values as CreateCompanyPayload);
        } else {
          handleCreate(values as CreateCompanyPayload);
        }
      }}
    >
      {(formikProps) => {
        const {
          errors: { name: nameError },
          touched: { name: nameTouched },
        } = formikProps;
        return (
          <Grid container={true} spacing={3} data-testid="company-form">
            <Grid item={true} md={6} xs={12}>
              <TextField
                variant="outlined"
                label="Name *"
                type="text"
                fullWidth={true}
                error={nameTouched && !!nameError}
                {...formikProps.getFieldProps('name')}
              />
            </Grid>
            <Grid item={true} md={6} xs={12}>
              <CountrySelect
                value={formikProps.getFieldProps('country').value}
                onChange={(newValue: CountryType | null) => {
                  formikProps.setFieldValue('country', newValue ? newValue.code : null, true);
                }}
              />
            </Grid>
            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="State/Region" type="text" fullWidth={true} {...formikProps.getFieldProps('state')} />
            </Grid>
            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="City" type="text" fullWidth={true} {...formikProps.getFieldProps('city')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="Address 1" type="text" fullWidth={true} {...formikProps.getFieldProps('address_1')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="Address 2" type="text" fullWidth={true} {...formikProps.getFieldProps('address_2')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="Tax ID" type="text" fullWidth={true} {...formikProps.getFieldProps('tax_id')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="SDI" type="text" fullWidth={true} {...formikProps.getFieldProps('sdi')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField variant="outlined" label="Phone number" type="text" fullWidth={true} {...formikProps.getFieldProps('phone')} />
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField
                id="size-select"
                data-testid="size-select"
                select
                fullWidth={true}
                value={formikProps.getFieldProps('size').value}
                onChange={(event) => {
                  formikProps.setFieldValue('size', event.target.value, true);
                }}
              >
                {sizeListMap.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item={true} md={6} xs={12}>
              <TextField
                id="usage-select"
                select
                fullWidth={true}
                value={formikProps.getFieldProps('usage').value}
                onChange={(event) => {
                  formikProps.setFieldValue('usage', event.target.value, true);
                }}
              >
                {usageListMap.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item={true} xs={12}>
              <Row hAlign="flex-end">
                <ButtonComponent
                  buttontype="cancel"
                  onClick={reducedVersion ? () => formikProps.handleReset() : goBack}
                  sx={{ marginRight: theme.spacing(3) }}
                />

                <ButtonComponent
                  buttontype="mainaction"
                  text="Save"
                  onClick={() => formikProps.submitForm()}
                  disabled={updatedCompanyAdmin.isLoading || createdCompanyAdmin.isLoading}
                />
              </Row>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );

  const content = reducedVersion ? (
    <Box data-testid="reduced-version-wrapper">
      <ReducedContainer>{formCore}</ReducedContainer>
    </Box>
  ) : (
    <DashboardPageLayout title={companyData ? 'Company' : 'Add new Company'}>
      <AlertBox messages={errorMessages} onMessageDismiss={handleErrorMessageDismiss} />
      <Box data-testid="not-reduced-version-wrapper">
        <Container>{formCore}</Container>
      </Box>
      <SnackBarCompleteHandler
        isSuccess={updatedCompanyAdmin.isSuccess}
        isError={updatedCompanyAdmin.isError}
        error={updatedCompanyAdmin.error}
        successMessage="Company successfully updated"
        onSuccess={() => sendFeedBack?.(true)}
        onError={() => sendFeedBack?.(false)}
      />
      <SnackBarCompleteHandler
        isSuccess={createdCompanyAdmin.isSuccess}
        isError={createdCompanyAdmin.isError}
        error={createdCompanyAdmin.error}
        successMessage="Company successfully created"
      />
    </DashboardPageLayout>
  );

  return content;
};
